// El borde con el silo de reportes: traduce su vocabulario al de la pantalla.
import type {
    ReportCatalog,
    ReportCatalogKpis,
    ReportCatalogWire,
    ReportDefinition,
    ReportDefinitionWire,
    ReportRunLogEntry,
    ReportRunResult,
    ReportRunResultWire,
    ReportRunWire,
} from './reportsContract';

/** Espejo de `MAX_RUNS` del repositorio del silo: el log nunca trae más de 50 corridas. */
const RUN_LOG_CAP = 50;
const RECENT_WINDOW_DAYS = 30;
const DAY_MS = 86_400_000;

function toDefinition(wire: ReportDefinitionWire): ReportDefinition {
    return {
        reportId: wire.reportId,
        name: wire.name,
        description: wire.description,
        reference: wire.reference,
        periodicity: wire.periodicity,
        formats: wire.formats,
        lastRunAt: wire.lastRunAt,
    };
}

/** El silo manda solo el `reportId` en cada corrida; el nombre sale del catálogo de la misma respuesta. */
function toLogEntry(wire: ReportRunWire, nameByReportId: Map<string, string>): ReportRunLogEntry {
    return {
        runId: wire.runId,
        reportId: wire.reportId,
        // Vacío, no un nombre plausible: una corrida de un reporte fuera del catálogo es un dato a
        // mostrar, no a maquillar.
        reportName: nameByReportId.get(wire.reportId) ?? '',
        requestedAt: wire.requestedAt,
        format: wire.format,
        status: wire.status,
        requestedByName: wire.requestedByName,
        rowCount: wire.rowCount,
        failureReason: wire.failureReason,
    };
}

function toKpis(
    definitions: ReportDefinition[],
    runs: ReportRunWire[],
    now: number,
): ReportCatalogKpis {
    const since = now - RECENT_WINDOW_DAYS * DAY_MS;
    return {
        total: definitions.length,
        daily: definitions.filter((d) => d.periodicity === 'DAILY').length,
        weekly: definitions.filter((d) => d.periodicity === 'WEEKLY').length,
        monthly: definitions.filter((d) => d.periodicity === 'MONTHLY').length,
        runsLast30d: runs.filter((r) => Date.parse(r.requestedAt) >= since).length,
        runsTruncated: runs.length >= RUN_LOG_CAP,
    };
}

/** `{definitions, runs}` del silo → lo que la pantalla dibuja. Tira si la forma no es la esperada. */
export function toReportCatalog(wire: ReportCatalogWire, now: number = Date.now()): ReportCatalog {
    if (!Array.isArray(wire.definitions) || !Array.isArray(wire.runs)) {
        throw new Error(
            'El catálogo de reportes no trajo `definitions` y `runs`. El contrato del silo cambió.',
        );
    }
    const reports = wire.definitions.map(toDefinition);
    const nameByReportId = new Map(reports.map((r) => [r.reportId, r.name]));
    return {
        kpis: toKpis(reports, wire.runs, now),
        reports,
        recentRuns: wire.runs.map((run) => toLogEntry(run, nameByReportId)),
    };
}

/** El `201` del `run` → el archivo listo para bajar, con el estado real de la corrida. */
export function toReportRunResult(wire: ReportRunResultWire): ReportRunResult {
    // `run` se re-tipa como opcional: el wire viene de la red y el tipo es una promesa, no un hecho.
    const run = wire.run as ReportRunResultWire['run'] | undefined;
    if (!run || typeof wire.content !== 'string' || typeof wire.filename !== 'string') {
        throw new Error(
            'La corrida respondió sin `run`/`content`/`filename`: no hay archivo que descargar.',
        );
    }
    return {
        runId: run.runId,
        reportId: run.reportId,
        status: run.status,
        rowCount: run.rowCount,
        failureReason: run.failureReason,
        filename: wire.filename,
        contentType: wire.contentType,
        content: wire.content,
    };
}
